import { useRef, useEffect, useState } from 'react';
import PolygonMode from './PolygonMode';
import MapboxDraw, { DrawPolygon } from '@mapbox/mapbox-gl-draw';
import { EventHandler } from './Map';
import bbox from '@turf/bbox';
import * as MapboxGl from 'mapbox-gl';
import _, { cloneDeepWith } from 'lodash';
//@ts-ignore
import StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import { colors } from '../../../styles/theme';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { drawFilterSelector, drawToolDrawnState } from '../../../recoil/atoms';
import { DrawFilter } from '../../../contexts/FilterContext/types';

export interface DrawFeature extends DrawPolygon {
  id: string;
  existing?: boolean;
}

const useDrawFeature = (mapRef: MapboxGl.Map | null) => {
  const drawRef = useRef<null | MapboxDraw>(null);
  const drawToolEnabledRef = useRef<boolean>(false);
  const [drawFilter, setDrawFilter] = useRecoilState(drawFilterSelector);
  const setDrawToolDrawn = useSetRecoilState(drawToolDrawnState);
  const drawFilterRef = useRef<DrawFilter | null>(null);
  const [isDrawActive, setIsDrawActive] = useState(false);
  drawFilterRef.current = drawFilter;
  drawToolEnabledRef.current = drawFilter.toolEnabled;
  useEffect(() => {
    if (mapRef) {
      mapRef.on('move', (evt: EventHandler) => {
        if (drawRef.current && drawToolEnabledRef.current && drawRef.current.getMode() !== 'draw_polygon') {
          drawRef.current && drawRef.current.changeMode('draw_polygon');
        }
      });
      mapRef.on('draw.update', action => {
        if (drawFilterRef.current && !drawFilterRef.current.enabled && drawToolEnabledRef.current) {
          const features = [...drawFilterRef.current.features];
          action.features.bbox = bbox(action.features);
          features.push(action.features);
          setDrawFilter({
            ...drawFilterRef.current,
            features: features.map(f => cloneDeepWith(f, (v, k) => (typeof v === 'function' || k === 'ctx') ? null : undefined)),
            toolEnabled: false,
            enabled: true,
          });
          setDrawToolDrawn(true);
        }
      });
      mapRef.on('polygonMode.drawActive', action => {
        if (drawToolEnabledRef.current) {
          setIsDrawActive(action.active);
        }
      });
      const Draw = new MapboxDraw({
        modes: { ...MapboxDraw.modes, draw_polygon: PolygonMode, static: StaticMode },
        userProperties: true,
        styles: [
          {
            id: 'gl-draw-line',
            type: 'line',
            filter: ['all', ['==', '$type', 'LineString']],
            layout: {
              'line-cap': 'round',
              'line-join': 'round',
            },
            paint: {
              'line-color': ['get', 'user_color'],
              'line-width': 3,
            },
          },
          {
            id: 'gl-draw-polygon-stroke',
            type: 'line',
            filter: ['all', ['==', '$type', 'Polygon']],
            layout: {
              'line-cap': 'round',
              'line-join': 'round',
            },
            paint: {
              'line-color': ['get', 'user_color'],
              'line-width': 3,
            },
          },
          {
            id: 'gl-draw-polygon-and-line-vertex-stroke-inactive',
            type: 'circle',
            filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
            paint: {
              'circle-radius': 5,
              'circle-color': colors.white,
            },
          },
          {
            id: 'gl-draw-polygon-and-line-vertex-inactive',
            type: 'circle',
            filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
            paint: {
              'circle-radius': 3,
              'circle-color': colors.brightOrange,
            },
          },
        ],
      });
      mapRef.addControl(Draw);
      drawRef.current = Draw;
    }
  }, [mapRef]);
  useEffect(() => {
    if (drawFilter && drawFilter.toolEnabled) {
      drawRef.current && drawRef.current.changeMode('draw_polygon');
    }
    if ((!drawFilter || !drawFilter.toolEnabled) && drawRef.current && drawRef.current.getMode() === 'draw_polygon') {
      drawRef.current.changeMode('simple_select');
    }
    if (mapRef && drawFilter && drawRef.current) {
      let items: any[] = [];
      drawFilter.features.forEach((feature: DrawFeature) => {
        if (feature.id && drawRef.current && !drawRef.current.get(feature.id)) {
          let ob: Record<string, any> = {};
          _.forEach(feature, (value, key) => {
            if (feature.hasOwnProperty(key) && key != 'ctx') {
              ob[key] = value;
            }
          });
          ob.geometry = {
            type: ob.type,
            coordinates: ob.coordinates,
          };
          items.push(ob);
        }
      });
      if (items.length) {
        drawRef.current.add({
          type: 'FeatureCollection',
          features: items,
        });
      }
    }
  }, [drawFilter, drawRef.current, mapRef]);

  const applyDrawFilter = () => {
    setDrawFilter({
      ...drawFilter,
      toolEnabled: false,
      enabled: true,
      features: drawFilter.features.map((item) => {
        return { ...item, properties: { color: colors.cyan } };
      }),
    });
  };
  const undoRecentChanges = () => {
    if (mapRef && drawFilter && drawRef.current) {
      const items = drawFilter.features.filter(feature => !feature.existing).map(feature => feature.id);
      if (items.length) {
        drawRef.current.delete(items);
        drawRef.current.changeMode('static');
      }
      const features = drawFilter.features.filter(
        (item: Record<string, any>) => !(items || []).find((id: string) => id === item.id)
      );
      setDrawFilter({ ...drawFilter, features, toolEnabled: false, enabled: true });
    }
    setDrawFilter({ ...drawFilter, features: [], toolEnabled: false, enabled: false });
  };

  const clearAll = () => {
    if (mapRef && drawFilter && drawRef.current) {
      drawRef.current.deleteAll();
      setDrawFilter({ ...drawFilter, features: [] });
    }
    setDrawFilter({ ...drawFilter, features: [] });
    setDrawToolDrawn(false);
    setIsDrawActive(false);
  };
  return { applyDrawFilter, undoRecentChanges, clearAll, isDrawActive };
};

export default useDrawFeature;
